import {
  createContext,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { Dialog, DialogProps } from "../../components/Dialog";
import { ConfirmCancelledError } from "./error";

export type ConfirmOptions = Omit<DialogProps, "open">;

export type Confirm = (confirmOptions: ConfirmOptions) => Promise<void>;
export type ConfirmContextValue = Confirm & { confirm: Confirm; handleClose: () => void };

const getDefaultContextValue = (): ConfirmContextValue => {
  const fallbackConfirm: Confirm = async () => {};
  return Object.assign(fallbackConfirm, {
    confirm: fallbackConfirm,
    handleClose: () => {},
  });
};

const ConfirmContext = createContext<ConfirmContextValue>(getDefaultContextValue());

export type ConfirmProviderProps = PropsWithChildren<EmptyObject>;

export const ConfirmProvider = ({ children }: ConfirmProviderProps) => {
  const [options, setOptions] = useState<ConfirmOptions | undefined>(undefined);

  const [resolveReject, setResolveReject] = useState<[() => void, (reason?: Error) => void] | []>([]);

  const [resolve, reject] = resolveReject;

  const handleClose = useCallback(() => {
    setResolveReject([]);
  }, []);

  const handleCancel = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      options?.secondaryButton?.onClick?.(event);
      if (reject) {
        reject(new ConfirmCancelledError());
      }
      handleClose();
    },
    [handleClose, options, reject]
  );

  const handleConfirm = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      options?.primaryButton?.onClick?.(event);
      if (resolve) {
        resolve();
      }
      handleClose();
    },
    [handleClose, options, resolve]
  );

  const confirm = useCallback<Confirm>(
    (confirmOptions: ConfirmOptions) =>
      new Promise((resolve, reject) => {
        setOptions(confirmOptions);

        setResolveReject([resolve, reject]);
      }),
    []
  );

  const contextValue = useMemo<ConfirmContextValue>(
    () =>
      Object.assign(confirm, {
        confirm,
        handleClose,
      }),
    [confirm, handleClose]
  );

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}

      {options && (
        <Dialog
          open={resolveReject.length === 2}
          disableCloseOnEscape={options.disableCloseOnEscape}
          title={options.title}
          primaryMessage={options.primaryMessage}
          secondaryMessage={options.secondaryMessage}
          externalLinkButton={options.externalLinkButton}
          isVerticalButton={options.isVerticalButton}
          primaryButton={
            options.primaryButton && {
              ...options.primaryButton,
              onClick: handleConfirm,
            }
          }
          secondaryButton={
            options.secondaryButton && {
              ...options.secondaryButton,
              onClick: handleCancel,
            }
          }
        >
          {options.children}
        </Dialog>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => useContext(ConfirmContext);

export * from "./error";
